/* Evergleam core: event bus, helpers, money, storage, countdown, reveal, cart API.
   Loaded deferred on every page; every other module depends on window.EG. */
(function () {
  const EG = (window.EG = window.EG || {});
  const listeners = new Map();

  EG.on = (evt, fn) => {
    if (!listeners.has(evt)) listeners.set(evt, new Set());
    listeners.get(evt).add(fn);
    return () => listeners.get(evt).delete(fn);
  };
  EG.emit = (evt, data) => {
    (listeners.get(evt) || []).forEach((fn) => {
      try { fn(data); } catch (err) { console.error(`[EG] ${evt} handler failed`, err); }
    });
  };

  /* ---------- motion preference ---------- */
  const rmQuery = matchMedia('(prefers-reduced-motion: reduce)');
  const setMotion = () => {
    EG.motion = !rmQuery.matches && !(EG.cfg && EG.cfg.reduceMotion);
    document.documentElement.classList.toggle('rm', !EG.motion);
  };
  setMotion();
  rmQuery.addEventListener('change', () => { setMotion(); EG.emit('motion', EG.motion); });

  /* ---------- helpers ---------- */
  EG.store = {
    get(kind, key, fallback = null) {
      try { const v = window[kind].getItem(key); return v === null ? fallback : JSON.parse(v); } catch (e) { return fallback; }
    },
    set(kind, key, value) {
      try { window[kind].setItem(key, JSON.stringify(value)); } catch (e) { /* storage blocked: degrade silently */ }
    }
  };
  EG.debounce = (fn, ms = 300) => { let t; return (...a) => { clearTimeout(t); t = setTimeout(() => fn(...a), ms); }; };
  EG.idle = (fn) => ('requestIdleCallback' in window ? requestIdleCallback(fn, { timeout: 2000 }) : setTimeout(fn, 250));
  EG.near = (el, fn, margin = '300px') => {
    const io = new IntersectionObserver((es) => { if (es.some((e) => e.isIntersecting)) { io.disconnect(); fn(); } }, { rootMargin: margin });
    io.observe(el);
  };

  /** Format integer cents with the shop's money format. */
  EG.money = (cents) => {
    const fmt = (EG.cfg && EG.cfg.moneyFormat) || '${{amount}}';
    const group = (n, d, th, dec) => {
      const [w, f] = (n / 100).toFixed(d).split('.');
      return w.replace(/\B(?=(\d{3})+(?!\d))/g, th) + (f ? dec + f : '');
    };
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (_, k) => ({
      amount_no_decimals: group(cents, 0, ',', '.'),
      amount_with_comma_separator: group(cents, 2, '.', ','),
      amount_no_decimals_with_comma_separator: group(cents, 0, '.', ',')
    }[k] || group(cents, 2, ',', '.')));
  };

  /** Discount math identical to Shopify: the discount amount is rounded. */
  EG.discounted = (cents, pct) => cents - Math.round((cents * pct) / 100);

  EG.copy = async (text, btn) => {
    const label = btn.textContent;
    try { await navigator.clipboard.writeText(text.trim()); btn.textContent = 'Copied ✓'; }
    catch (e) { btn.textContent = text.trim(); }
    setTimeout(() => { btn.textContent = label; }, 1800);
  };

  EG.applyCode = async (code) => {
    EG.store.set('localStorage', 'eg:code', code);
    const res = await fetch(`/discount/${encodeURIComponent(code.trim())}`, { credentials: 'same-origin' });
    if (!res.ok) throw new Error('apply failed');
    EG.emit('cart:refresh');
  };

  EG.trap = (box) => {
    const sel = 'a[href],button:not([disabled]),input:not([disabled]),select,textarea,[tabindex]:not([tabindex="-1"])';
    const onKey = (e) => {
      if (e.key !== 'Tab') return;
      const f = [...box.querySelectorAll(sel)].filter((n) => n.offsetParent !== null);
      if (!f.length) return;
      if (e.shiftKey && document.activeElement === f[0]) { e.preventDefault(); f[f.length - 1].focus(); }
      else if (!e.shiftKey && document.activeElement === f[f.length - 1]) { e.preventDefault(); f[0].focus(); }
    };
    box.addEventListener('keydown', onKey);
    return () => box.removeEventListener('keydown', onKey);
  };

  /** Time left until 23:59:59 local on YYYY-MM-DD. */
  EG.until = (iso) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso || '');
    if (!m) return null;
    const end = new Date(+m[1], +m[2] - 1, +m[3], 23, 59, 59);
    const ms = end - Date.now();
    return { ms, end, d: Math.floor(ms / 864e5), h: Math.floor((ms % 864e5) / 36e5), m: Math.floor((ms % 36e5) / 6e4), s: Math.floor((ms % 6e4) / 1e3) };
  };

  /* <eg-countdown data-deadline="YYYY-MM-DD">: fills [data-u=d|h|m|s], [data-short], [data-date];
     toggles [data-before]/[data-after]. Real deadlines only — never loops. */
  customElements.define('eg-countdown', class extends HTMLElement {
    connectedCallback() { this.tick(); this.t = setInterval(() => this.tick(), this.querySelector('[data-u="s"]') ? 1000 : 30000); }
    disconnectedCallback() { clearInterval(this.t); }
    tick() {
      const l = EG.until(this.dataset.deadline);
      if (!l) { this.hidden = true; clearInterval(this.t); return; }
      const over = l.ms <= 0;
      this.querySelectorAll('[data-before]').forEach((n) => { n.hidden = over; });
      this.querySelectorAll('[data-after]').forEach((n) => { n.hidden = !over; });
      if (over) { clearInterval(this.t); return; }
      const pad = (n) => String(n).padStart(2, '0');
      this.querySelectorAll('[data-u]').forEach((n) => { n.textContent = pad(l[n.dataset.u]); });
      this.querySelectorAll('[data-short]').forEach((n) => { n.textContent = l.d > 0 ? `${l.d}d ${l.h}h` : `${l.h}h ${l.m}m`; });
      this.querySelectorAll('[data-date]').forEach((n) => { n.textContent = l.end.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' }); });
    }
  });

  /* ---------- cart API (one queue, Section Rendering API) ---------- */
  const SECTIONS = ['cart-drawer', 'cart-bubble'];
  let queue = Promise.resolve();
  const enqueue = (task) => { const run = queue.then(task, task); queue = run.catch(() => {}); return run; };
  const friendly = (status, msg) => {
    if (status === 422) {
      if (/sold out/i.test(msg)) return 'That one just sold out — we updated your cart.';
      const n = (msg || '').match(/\d+/);
      return n ? `Only ${n[0]} left — we added as many as we could.` : msg;
    }
    if (status === 429) return 'Busy moment — please try again in a second.';
    return 'Something went wrong. Please try again.';
  };
  async function post(url, body) {
    const res = await fetch(url, {
      method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ ...body, sections: SECTIONS, sections_url: location.pathname })
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw Object.assign(new Error(friendly(res.status, data.description || data.message)), { status: res.status });
    return data;
  }
  EG.cart = {
    add: (items) => enqueue(async () => {
      try {
        const data = await post('/cart/add.js', { items });
        EG.emit('cart:render', data.sections);
        EG.emit('cart:added', { items });
        if (EG.cfg.cartType === 'page') location.href = '/cart'; else EG.emit('cart:open');
        return data;
      } catch (err) {
        EG.emit('cart:error', err.message);
        if (err.status === 422) EG.emit('cart:refresh');
        throw err;
      }
    }),
    change: (key, quantity) => enqueue(async () => {
      try { const data = await post('/cart/change.js', { id: key, quantity }); EG.emit('cart:render', data.sections); }
      catch (err) { EG.emit('cart:error', err.message); EG.emit('cart:refresh'); }
    }),
    note: EG.debounce((note, done) => {
      fetch('/cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ note }) })
        .then((r) => done(r.ok)).catch(() => done(false));
    }, 600)
  };
  EG.on('cart:refresh', async () => {
    try { const r = await fetch(`${location.pathname}?sections=${SECTIONS.join(',')}`); EG.emit('cart:render', await r.json()); }
    catch (e) { /* offline: keep current drawer */ }
  });

  /* ---------- reveal on scroll ---------- */
  document.addEventListener('DOMContentLoaded', () => {
    const items = document.querySelectorAll('[data-reveal]');
    if (!EG.motion || !('IntersectionObserver' in window)) { items.forEach((n) => n.classList.add('in')); return; }
    const io = new IntersectionObserver((es) => es.forEach((e) => { if (e.isIntersecting) { e.target.classList.add('in'); io.unobserve(e.target); } }), { threshold: .12 });
    items.forEach((n) => io.observe(n));
  });

  /* ---------- back to top ---------- */
  document.addEventListener('DOMContentLoaded', () => {
    const btn = document.querySelector('[data-btt]');
    if (!btn) return;
    btn.hidden = false;
    const SHOW_AFTER = 600;
    let queued = false;
    const update = () => {
      queued = false;
      btn.classList.toggle('is-on', scrollY > SHOW_AFTER);
    };
    const onScroll = () => { if (!queued) { queued = true; requestAnimationFrame(update); } };
    addEventListener('scroll', onScroll, { passive: true });
    btn.addEventListener('click', () => {
      scrollTo({ top: 0, behavior: EG.motion ? 'smooth' : 'auto' });
      document.getElementById('Main')?.focus({ preventScroll: true });
    });
    update();
  });

  /* ---------- dark mode toggle ---------- */
  document.addEventListener('DOMContentLoaded', () => {
    const root = document.documentElement;
    const toggles = document.querySelectorAll('[data-theme-toggle]');
    const sync = () => toggles.forEach((t) => t.setAttribute('aria-pressed', String(root.dataset.theme === 'dark')));
    toggles.forEach((t) => t.addEventListener('click', () => {
      const dark = root.dataset.theme !== 'dark';
      if (EG.motion) { root.classList.add('theme-fade'); setTimeout(() => root.classList.remove('theme-fade'), 400); }
      if (dark) root.dataset.theme = 'dark'; else delete root.dataset.theme;
      try { localStorage.setItem('eg:theme', dark ? 'dark' : 'light'); } catch (e) { /* private mode: choice lasts this page only */ }
      sync();
    }));
    sync();
  });
})();
